'use client'

import { useState } from 'react'

import { motion } from 'framer-motion'
import { Trophy } from 'lucide-react'

import purpleLockCardIcon from '../assets/purple-background-purple-lock-icon.png'
import greenCheckIcon from '../assets/green-check.png'
import { usePassClaimReward } from '../hooks/usePassClaimReward'
import {
  highlightedTier,
  objectiveCountLabel,
  objectivesRemainingForTier,
  tierRewardSummary,
  tierState
} from '../lib/pass-ui'
import type { Pass, PassRewardTier } from '../types/pass'

import { Button } from '@/shared/ui/Button'

import { PassRewardRevealModal } from './PassRewardRevealModal'
import { RewardItemChip } from './RewardItemChip'
import { RewardStack } from './RewardVisual'

interface PassAvailableRewardCardProps {
  pass: Pass
}

export function PassAvailableRewardCard({ pass }: PassAvailableRewardCardProps) {
  const { mutate: claimReward, isPending } = usePassClaimReward()
  // Tier já resgatado, guardado para a revelação. Vem do resultado da mutation
  // (e não de `highlightedTier`), que após o refetch já aponta para o próximo.
  const [revealedTier, setRevealedTier] = useState<PassRewardTier | null>(null)
  const tier = highlightedTier(pass)

  if (!tier) {
    return (
      <section className="overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(135deg,#2C0E66_0%,#1C0843_55%,#160637_100%)] px-4 py-5 shadow-[0_20px_45px_rgba(8,3,24,0.4)] sm:px-7 sm:py-7">
        <div className="flex flex-col items-start gap-4 min-[390px]:flex-row min-[390px]:items-center sm:gap-6">
          <div className="flex size-24 shrink-0 items-center justify-center rounded-[1.5rem] bg-[#1A0838] p-3 sm:size-40 sm:p-4">
            <Trophy className="size-2/3 text-pass-gold" aria-hidden />
          </div>

          <div>
            <div className="flex items-center gap-3">
              <img src={greenCheckIcon} alt="Passe concluído" className="size-9 shrink-0 sm:size-10" />
              <h3 className="text-[1.3rem] font-black tracking-[-0.05em] text-white sm:text-[2.3rem]">
                Passe concluído
              </h3>
            </div>

            <p className="mt-3 text-[1rem] font-semibold tracking-[-0.03em] text-white/90 sm:mt-4 sm:text-[1.7rem]">
              Todas as recompensas deste passe já foram resgatadas.
            </p>
          </div>
        </div>
      </section>
    )
  }

  const state = tierState(tier)
  const available = state === 'available'
  const remainingObjectives = objectivesRemainingForTier(pass, tier)

  return (
    <section className="overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(135deg,#2C0E66_0%,#1C0843_55%,#160637_100%)] px-4 py-5 shadow-[0_20px_45px_rgba(8,3,24,0.4)] sm:px-7 sm:py-7">
      <div className="relative flex flex-col items-start gap-4 min-[390px]:flex-row min-[390px]:items-center sm:gap-6">
        <div className="absolute -left-10 top-4 size-32 rounded-full bg-[#7A2DFF]/20 blur-3xl" aria-hidden />

        <div className="relative shrink-0 rounded-[1.5rem] bg-[#1A0838] p-3 sm:p-4">
          {/* A recompensa chacoalha enquanto o resgate está em voo; a revelação
              em tamanho grande vem logo depois no PassRewardRevealModal. */}
          <motion.div
            animate={
              isPending
                ? { rotate: [0, -7, 7, -5, 5, 0], scale: [1, 1.06, 1.06, 1.04, 1] }
                : { rotate: 0, scale: 1 }
            }
            transition={
              isPending
                ? { duration: 0.6, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0.2 }
            }
          >
            <RewardStack
              items={tier.pass_reward_tier_items}
              frameClassName="size-20 sm:size-28"
              className={available ? undefined : 'opacity-60 grayscale'}
            />
          </motion.div>
        </div>

        <div className="relative min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <img
              src={available ? greenCheckIcon : purpleLockCardIcon}
              alt=""
              className="size-9 shrink-0 sm:size-10"
            />
            <h3 className="text-[1.3rem] font-black tracking-[-0.05em] text-white sm:text-[2.3rem]">
              {available ? 'Recompensa disponível' : 'Próxima recompensa'}
            </h3>
          </div>

          <p className="mt-3 text-[1rem] font-semibold tracking-[-0.03em] text-white/90 sm:mt-4 sm:text-[1.7rem]">
            {available ? (
              <>
                Sua recompensa em{' '}
                <span className="font-black text-[#FFD133]">
                  {tier.percent_threshold}%
                </span>{' '}
                já pode ser resgatada.
              </>
            ) : (
              <>
                Bloqueada até{' '}
                <span className="font-black text-[#FFD133]">
                  {tier.percent_threshold}%
                </span>{' '}
                de conclusão
              </>
            )}
          </p>

          <p className="mt-2 text-[0.95rem] font-semibold tracking-[-0.02em] text-[#B79BE8] sm:text-[1.35rem]">
            {available
              ? tierRewardSummary(tier)
              : `Conclua ${objectiveCountLabel(remainingObjectives)} para desbloquear.`}
          </p>

          {available ? (
            <div className="mt-4 flex flex-wrap gap-3">
              {tier.pass_reward_tier_items.map(item => (
                <RewardItemChip
                  key={item.id}
                  item={item}
                  className="rounded-full bg-white/12 px-3 py-2 text-white"
                />
              ))}
            </div>
          ) : null}

          <div className="mt-5">
            {available ? (
              <Button
                variant="reward"
                className="w-full min-[390px]:w-full sm:w-auto sm:min-w-64"
                disabled={isPending}
                onClick={() =>
                  claimReward(tier, { onSuccess: setRevealedTier })
                }
              >
                {isPending ? 'Resgatando…' : 'Resgatar recompensa'}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {revealedTier ? (
        <PassRewardRevealModal
          items={revealedTier.pass_reward_tier_items}
          description={`Recompensa de ${revealedTier.percent_threshold}% do passe.`}
          onClose={() => setRevealedTier(null)}
        />
      ) : null}
    </section>
  )
}
